import eventBus from '@sd-utility/eventbus';
import { Colours } from '@tvg/fuse-design/colours';
import { ComplexProductcard, SaveForLater } from '@tvg/fuse-design/complex-product-card';
import { PLP_UPDATE_EVENT } from 'client/config';
import useGlobalConfig from 'client/state/GlobalConfig';
import { addRoundel, makeSimple, formatReviewCount } from 'client/utils/utils';
import {
  addProductToSaveForLater,
  addProductToSessionStorage,
  removeProductFromSaveForLater,
  removeProductFromSessionStorage,
} from 'common/save-for-later-service';
import { Product } from 'common/types/search-response';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

const ProductCardFuseStyled = styled(ComplexProductcard)`
  img {
    border: 1px solid ${Colours.greys.grey3};
  }

  & .fuse-save__icon:before {
    background: white;
    opacity: 1;
  }

  & .fuse-save__icon--saved:before {
    background: var(--color-brand-mid);
  }
`;

function ProductCardComponent({
  product,
  productIndex,
  isSaved,
  globalBrand,
  isSkeleton,
}: {
  product: Product;
  productIndex: number;
  isSaved: boolean;
  globalBrand?: string;
  isSkeleton: boolean;
}) {
  const { isLocal, isSFLVeryIEHeaderEnabled } = useGlobalConfig();
  const {
    product_id,
    catalogueNumber,
    variation_id,
    url,
    brand,
    title,
    image,
    price,
    productReviews,
    swatchSources,
    roundelId,
    promotionIdentifier,
  } = product;

  const isUkBrand = globalBrand !== 'veryireland';

  const swatches = swatchSources?.length > 1 ? swatchSources : [];

  const reviewsCount = productReviews?.reviewsCount;
  const formattedreviewCount = formatReviewCount(reviewsCount);
  const reviewText = formattedreviewCount ? `${formattedreviewCount} ${reviewsCount > 1 ? 'reviews' : 'review'}` : '';

  const displaySavePriceInd = price?.displaySavePriceInd;
  const priceShowSave = typeof displaySavePriceInd === 'string' ? displaySavePriceInd === 'true' : displaySavePriceInd;

  const isTopPriorityProduct = productIndex < 3;

  const titleMemo = useMemo(() => makeSimple(title), [title]);

  const imageMemo = useMemo(
    () => ({
      url: addRoundel(image?.url, roundelId, globalBrand),
      altText: makeSimple(image?.alt_text),
      disabledLazy: isTopPriorityProduct,
      fetchPriority: isTopPriorityProduct ? 'high' : 'auto',
    }),
    [image?.url, roundelId, globalBrand, image?.alt_text, isTopPriorityProduct],
  );

  const priceMemo = useMemo(
    () => ({
      current: price?.current,
      previous: price?.previous,
      saving: price?.saving,
      showCurrentPriceFrom: price?.currentFrom,
      showPreviousPriceFrom: price?.previousFrom,
      showSave: priceShowSave,
      isEuro: price?.currencyCode === 'EUR',
    }),
    [price, priceShowSave],
  );

  const starsMemo = useMemo(
    () => ({
      rating: productReviews?.averageStars,
      reviewText,
    }),
    [productReviews?.averageStars, reviewText],
  );

  const handleSaveClick = useCallback(
    async (newSaved: boolean) => {
      eventBus.publish(PLP_UPDATE_EVENT);
      const productIDString = product_id.toString();
      try {
        if (newSaved) {
          await addProductToSaveForLater(isLocal, productIDString);
          addProductToSessionStorage(productIDString);
        } else {
          await removeProductFromSaveForLater(isLocal, productIDString);
          removeProductFromSessionStorage(productIDString);
        }
      } catch (error) {
        console.error('Error updating save for later:', error);
      }
    },
    [isLocal, product_id],
  );

  const sflMemo = useMemo<SaveForLater>(
    () => ({
      isSaved,
      saveCallback: handleSaveClick,
    }),
    [isSaved, handleSaveClick],
  );

  const promotionIdentifierMemo = useMemo(
    () => ({
      colour: promotionIdentifier?.colour || '',
      displayName: promotionIdentifier?.displayName || '',
    }),
    [promotionIdentifier?.colour, promotionIdentifier?.displayName],
  );
  return (
    <ProductCardFuseStyled
      catalogueId={catalogueNumber}
      productUrl={url}
      isSkeleton={isSkeleton}
      brand={brand}
      title={titleMemo}
      image={imageMemo}
      swatches={swatches}
      price={priceMemo}
      stars={starsMemo}
      sfl={(isUkBrand || isSFLVeryIEHeaderEnabled) && sflMemo}
      promotionIdentifier={promotionIdentifierMemo}
      dataTestId="gallery-product-card"
      data-cnstrc-item-id={product_id}
      data-cnstrc-item-name={titleMemo}
      data-cnstrc-item-variation-id={variation_id}
      data-cnstrc-item-price={price?.current}
    />
  );
}

export const ProductCard = React.memo(ProductCardComponent);
